import { uuidv7 } from 'uuidv7';
import type { ControlMessage } from '@/contracts';
import type {
  AttemptResult,
  TeleopAttemptWsMeta,
  TeleopSessionEventEntry,
} from './teleopAttemptTypes';

export const buildAttemptMessageMeta = (idempotencyKey: string) => {
  const messageId = uuidv7();
  const meta = {
    message_id: messageId,
    idempotency_key: idempotencyKey,
  };

  return meta;
};

export const getAttemptMessageDurationSeconds = (
  message: ControlMessage,
): number | undefined => {
  const durationFromSeconds =
    'duration_seconds' in message ? message.duration_seconds : undefined;
  const durationFromDuration =
    'duration' in message ? message.duration : undefined;
  const durationFromLegacySeconds =
    'seconds' in message ? message.seconds : undefined;
  const rawDuration =
    durationFromSeconds ??
    durationFromDuration ??
    durationFromLegacySeconds;

  if (typeof rawDuration === 'number') {
    const isFiniteDuration = Number.isFinite(rawDuration);

    if (!isFiniteDuration) {
      return undefined;
    }

    return rawDuration;
  }

  if (typeof rawDuration !== 'string') {
    return undefined;
  }

  const parsedDuration = Number.parseFloat(rawDuration);
  const isFiniteDuration = Number.isFinite(parsedDuration);

  if (!isFiniteDuration) {
    return undefined;
  }

  return parsedDuration;
};

export const parseAttemptResultSuccess = (
  message: ControlMessage,
): boolean | null => {
  const hasSuccessField = 'success' in message;

  if (!hasSuccessField) {
    return null;
  }

  const rawSuccess = message.success;

  if (typeof rawSuccess === 'boolean') {
    return rawSuccess;
  }

  if (typeof rawSuccess !== 'string') {
    return null;
  }

  const normalizedSuccess = rawSuccess.toLowerCase();
  const isSuccessful = ['success', 'win', 'won', 'true', '1', 'yes'].includes(
    normalizedSuccess,
  );
  const isFailure = ['fail', 'loss', 'lost', 'false', '0', 'no'].includes(
    normalizedSuccess,
  );

  if (isSuccessful) {
    return true;
  }

  if (isFailure) {
    return false;
  }

  return null;
};

export const buildAttemptResultFromMessage = (
  message: ControlMessage,
): AttemptResult | null => {
  const parsedSuccess = parseAttemptResultSuccess(message);

  if (parsedSuccess === null) {
    return null;
  }

  const reason =
    typeof message.reason === 'string' ? message.reason : undefined;
  const attemptId =
    typeof message.attempt_id === 'string' ? message.attempt_id : undefined;
  const robotId =
    typeof message.robot_id === 'string' ? message.robot_id : undefined;
  const robotName =
    typeof message.robot_name === 'string' ? message.robot_name : undefined;
  const timestamp =
    typeof message.timestamp === 'string' ? message.timestamp : undefined;
  const nextAttemptResult = {
    success: parsedSuccess,
    reason,
    attemptId,
    robotId,
    robotName,
    timestamp,
  };

  return nextAttemptResult;
};

export const buildAttemptWsMeta = (
  message: ControlMessage,
): TeleopAttemptWsMeta => {
  const messageType = 'type' in message ? message.type : undefined;
  const sessionId =
    'session_id' in message && typeof message.session_id === 'string'
      ? message.session_id
      : undefined;
  const attemptId =
    'attempt_id' in message && typeof message.attempt_id === 'string'
      ? message.attempt_id
      : undefined;
  const durationSeconds = getAttemptMessageDurationSeconds(message);
  const nextMeta = {
    type: messageType,
    sessionId,
    attemptId,
    durationSeconds,
  };

  return nextMeta;
};

export const buildSessionEventEntry = (
  message: ControlMessage,
): TeleopSessionEventEntry => {
  const eventType =
    'event_type' in message && typeof message.event_type === 'string'
      ? message.event_type
      : 'session_event';
  const attemptId =
    'payload' in message &&
    typeof message.payload?.attempt_id === 'string'
      ? message.payload.attempt_id
      : undefined;
  const payloadTimestamp =
    'payload' in message && typeof message.payload?.ts === 'string'
      ? message.payload.ts
      : undefined;
  const timestamp = payloadTimestamp ?? new Date().toISOString();
  const nextEntry = {
    ts: timestamp,
    eventType,
    attemptId,
  };

  return nextEntry;
};

export const getSessionEventIdempotencyKey = (
  message: ControlMessage,
  sessionId: string,
) => {
  const explicitIdempotencyKey =
    'idempotency_key' in message && typeof message.idempotency_key === 'string'
      ? message.idempotency_key
      : '';
  const hasExplicitIdempotencyKey = explicitIdempotencyKey.length > 0;

  if (hasExplicitIdempotencyKey) {
    return explicitIdempotencyKey;
  }

  const streamValue =
    'stream' in message && typeof message.stream === 'string'
      ? message.stream
      : '';
  const streamIdValue =
    'stream_id' in message && typeof message.stream_id === 'string'
      ? message.stream_id
      : '';
  const eventTypeValue =
    'event_type' in message && typeof message.event_type === 'string'
      ? message.event_type
      : 'session_event';
  const payloadAttemptIdValue =
    'payload' in message && typeof message.payload?.attempt_id === 'string'
      ? message.payload.attempt_id
      : '';
  const payloadTimestampValue =
    'payload' in message && typeof message.payload?.ts === 'string'
      ? message.payload.ts
      : '';
  const hasStreamId = streamIdValue.length > 0;

  if (hasStreamId) {
    const streamIdempotencyKey = `session_event:${streamValue}:${streamIdValue}`;

    return streamIdempotencyKey;
  }

  const fallbackIdempotencyKey =
    `session_event:${eventTypeValue}:${sessionId}:${payloadAttemptIdValue}:${payloadTimestampValue}`;

  return fallbackIdempotencyKey;
};

export const getAttemptWaitReadyIdempotencyKey = (
  message: ControlMessage,
  sessionId: string,
) => {
  const messageId =
    'message_id' in message && typeof message.message_id === 'string'
      ? message.message_id
      : '';
  const hasMessageId = messageId.length > 0;

  if (hasMessageId) {
    return `message:${messageId}`;
  }

  const explicitIdempotencyKey =
    'idempotency_key' in message && typeof message.idempotency_key === 'string'
      ? message.idempotency_key
      : '';
  const hasExplicitIdempotencyKey = explicitIdempotencyKey.length > 0;

  if (hasExplicitIdempotencyKey) {
    return explicitIdempotencyKey;
  }

  const messageType = 'type' in message ? message.type : '';

  if (messageType === 'attempt_ready') {
    return `attempt_ready:${sessionId}`;
  }

  const waitReason =
    'reason' in message && typeof message.reason === 'string'
      ? message.reason
      : '';
  const fallbackIdempotencyKey = `attempt_wait:${sessionId}:${waitReason}`;

  return fallbackIdempotencyKey;
};

export const getAttemptStartIdempotencyKey = (
  message: ControlMessage,
  sessionId: string,
) => {
  const explicitIdempotencyKey =
    'idempotency_key' in message && typeof message.idempotency_key === 'string'
      ? message.idempotency_key
      : '';
  const hasExplicitIdempotencyKey = explicitIdempotencyKey.length > 0;

  if (hasExplicitIdempotencyKey) {
    return explicitIdempotencyKey;
  }

  const messageId =
    'message_id' in message && typeof message.message_id === 'string'
      ? message.message_id
      : '';
  const hasMessageId = messageId.length > 0;

  if (hasMessageId) {
    return `message:${messageId}`;
  }

  const attemptId =
    'attempt_id' in message && typeof message.attempt_id === 'string'
      ? message.attempt_id
      : '';
  const hasAttemptId = attemptId.length > 0;

  if (hasAttemptId) {
    return `attempt_started:${sessionId}:${attemptId}`;
  }

  const attemptNumber =
    'attempt_no' in message && typeof message.attempt_no === 'number'
      ? message.attempt_no
      : 'unknown';
  const fallbackIdempotencyKey =
    `attempt_started:${sessionId}:${attemptNumber}`;

  return fallbackIdempotencyKey;
};

export const getAttemptResultIdempotencyKey = (message: ControlMessage) => {
  const explicitIdempotencyKey =
    'idempotency_key' in message && typeof message.idempotency_key === 'string'
      ? message.idempotency_key
      : '';
  const hasExplicitIdempotencyKey = explicitIdempotencyKey.length > 0;

  if (hasExplicitIdempotencyKey) {
    return explicitIdempotencyKey;
  }

  const sessionId =
    'session_id' in message && typeof message.session_id === 'string'
      ? message.session_id
      : null;
  const attemptId =
    'attempt_id' in message && typeof message.attempt_id === 'string'
      ? message.attempt_id
      : null;
  const hasSessionId = sessionId !== null;
  const hasAttemptId = attemptId !== null;

  if (!hasSessionId || !hasAttemptId) {
    return null;
  }

  const fallbackIdempotencyKey = `attempt_result:${sessionId}:${attemptId}`;

  return fallbackIdempotencyKey;
};
